import { countBy } from "lodash-es";

import type { Column, Data } from "./parser";

export interface Validation {
  valid: boolean;
  validRows: number[];
  invalidRows: number[];
  getInvalidError: (row: number, columnName: string) => string | undefined;
  getInvalidSummaryByColumn: (columnName: string) => {
    values: string[];
    rowCount: number;
  };
  getMissingError: (row: number, columnName: string) => string | undefined;
  getWarnings: (row: number, columnName: string) => string | undefined;
}

type DataLocation = `${number}-${string}`;

export function validate<TColumns extends readonly Column<any>[]>(columns: TColumns, data: Data<TColumns>): Validation {
  const missingColumns = new Map<DataLocation, string>();
  const invalidColumns = new Map<DataLocation, string>();
  const warningColumns = new Map<DataLocation, string>();

  const uniques: { [columnName: string]: { [value: string]: number } } = {};
  const listValidRowIndex: number[] = [];
  const listInvalidRowIndex: number[] = [];

  for (let y = 0; y < data.length; y++) {
    const row = data[y];
    let isRowInvalid = false;

    for (const { rules, warnings, name } of columns) {
      const columnName = name as keyof typeof row;
      const value = row[columnName];

      if (rules) {
        if ("required" in rules && !value) {
          const message = typeof rules.required === "function" ? rules.required(row) : rules.required;
          if (message) {
            missingColumns.set(`${y}-${columnName}`, message);
            isRowInvalid = true;
            continue;
          }
        }

        if ("match" in rules && value) {
          if (!rules.match.regex.test(value)) {
            invalidColumns.set(`${y}-${columnName}`, rules.match.error);
            isRowInvalid = true;
            continue;
          }
        }

        if ("oneOf" in rules && value) {
          if (typeof rules.oneOf.values !== "function" && !isOneOf(value, rules.oneOf.values, rules.oneOf.ignoreCase)) {
            invalidColumns.set(`${y}-${columnName}`, rules.oneOf.error);
            isRowInvalid = true;
            continue;
          }

          if (
            typeof rules.oneOf.values === "function" &&
            !isOneOf(value, rules.oneOf.values(row), rules.oneOf.ignoreCase)
          ) {
            invalidColumns.set(`${y}-${columnName}`, rules.oneOf.error);
            isRowInvalid = true;
            continue;
          }
        }

        if ("notOneOf" in rules && value) {
          if (isOneOf(value, rules.notOneOf.values, rules.notOneOf.ignoreCase)) {
            invalidColumns.set(`${y}-${columnName}`, rules.notOneOf.error);
            isRowInvalid = true;
            continue;
          }
        }

        if ("unique" in rules) {
          if (!uniques[columnName]) {
            uniques[columnName] = countBy(data, (x) =>
              rules.unique.ignoreCase ? x[columnName]?.toLowerCase() : x[columnName],
            );
          }

          const uniqueValue = rules.unique.ignoreCase ? value?.toLowerCase() : value;
          if (uniqueValue && uniques[columnName][uniqueValue] > 1 && "unique" in rules) {
            invalidColumns.set(`${y}-${columnName}`, rules.unique.error);
            isRowInvalid = true;
            continue;
          }
        }

        if ("validate" in rules) {
          if (!rules.validate.fn(value)) {
            invalidColumns.set(`${y}-${columnName}`, rules.validate.error);
            isRowInvalid = true;
            continue;
          }
        }
      }

      if (warnings) {
        if ("unique" in warnings) {
          if (!uniques[columnName]) {
            uniques[columnName] = countBy(data, (x) => x[columnName]);
          }

          if (value && uniques[columnName][value] > 1 && "unique" in warnings) {
            warningColumns.set(`${y}-${columnName}`, warnings.unique);
          }
        }

        if ("validate" in warnings) {
          if (!warnings.validate.fn(value)) {
            warningColumns.set(`${y}-${columnName}`, warnings.validate.error);
          }
        }
      }
    }

    if (isRowInvalid) {
      listInvalidRowIndex.push(y);
    } else {
      listValidRowIndex.push(y);
    }
  }

  function getInvalidError(row: number, columnName: string) {
    return invalidColumns.get(`${row}-${columnName}`);
  }

  function getInvalidSummaryByColumn(columnName: string) {
    const invalidValues: string[] = [];
    let invalidRowCount = 0;

    for (const [key, _] of invalidColumns.entries()) {
      const [currRow, currColumn] = key.split("-");

      if (currColumn === columnName) {
        const currInvalidValue = data[Number(currRow)][currColumn as keyof (typeof data)[0]];

        if (currInvalidValue) {
          invalidValues.push(currInvalidValue);
        }

        invalidRowCount++;
      }
    }

    return {
      values: Array.from(new Set(invalidValues)), // Unique values that are invalid
      rowCount: invalidRowCount, // Total amount of invalid rows for this column
    };
  }

  function getMissingError(row: number, columnName: string) {
    return missingColumns.get(`${row}-${columnName}`);
  }

  function getWarnings(row: number, columnName: string) {
    return warningColumns.get(`${row}-${columnName}`);
  }

  return {
    valid: missingColumns.size === 0 && invalidColumns.size === 0,
    validRows: Array.from(new Set(listValidRowIndex)),
    invalidRows: Array.from(new Set(listInvalidRowIndex)),
    getInvalidSummaryByColumn,
    getInvalidError,
    getMissingError,
    getWarnings,
  };
}

function isOneOf(value: string, values: readonly string[], ignoreCase = false) {
  value = ignoreCase ? value.toLowerCase() : value;

  return values.some((x) => {
    x = ignoreCase ? x.toLowerCase() : x;

    return x === value;
  });
}
